import { NoteEventBox, ValueEventBox } from "@opendaw/studio-boxes";
import { Interpolation } from "@opendaw/lib-dsp";
import { InterpolationFieldAdapter } from "@opendaw/studio-adapters";
import { asInstanceOf, isDefined, panic, UUID } from "@opendaw/lib-std";
import { Facade, Props } from "../Common";
import { Guard } from "../Guard";
export class NoteEventImpl extends Facade {
    static wrap(context, box) {
        return context.facade(box, () => new NoteEventImpl(context, box));
    }
    constructor(context, box) {
        super(context, box);
        this.bind({
            position: box.position, duration: box.duration, pitch: box.pitch, velocity: box.velocity,
            cents: box.cent, playCount: box.playCount, playCurve: box.playCurve, chance: box.chance
        });
    }
}
export class ValueEventImpl extends Facade {
    static wrap(context, box) {
        return context.facade(box, () => new ValueEventImpl(context, box));
    }
    constructor(context, box) {
        super(context, box);
        this.bind({ position: box.position, value: box.value });
    }
    get index() { return this.box.index.getValue(); }
    get interpolation() { return InterpolationFieldAdapter.read(this.box.interpolation); }
    set interpolation(value) {
        const validated = Interpolations.validate(value);
        this.context.edit(() => InterpolationFieldAdapter.write(this.box.interpolation, validated));
    }
}
export var Interpolations;
(function (Interpolations) {
    Interpolations.validate = (value, name = "interpolation") => {
        if (!isDefined(value) || typeof value !== "object") {
            return panic(new TypeError(`${name}: expected Interpolation.None, Interpolation.Linear or Interpolation.Curve(slope)`));
        }
        const { type, slope } = value;
        if (type === "none") {
            return Interpolation.None;
        }
        if (type === "linear") {
            return Interpolation.Linear;
        }
        if (type === "curve") {
            return Interpolation.Curve(Guard.float32("unipolar", slope, `${name}.slope`));
        }
        return panic(new TypeError(`${name}: unknown interpolation type ${Guard.describe(type)}`));
    };
})(Interpolations || (Interpolations = {}));
export class NoteEvents {
    #context;
    #collection;
    constructor(context, collection) {
        this.#context = context;
        this.#collection = collection;
    }
    get collection() { return this.#collection; }
    list() {
        return this.#collection.events.pointerHub.incoming()
            .map(({ box }) => NoteEventImpl.wrap(this.#context, asInstanceOf(box, NoteEventBox)))
            .sort((a, b) => a.position - b.position || a.pitch - b.pitch);
    }
    add(props) {
        return this.#context.edit(() => {
            const box = NoteEventBox.create(this.#context.boxGraph, UUID.generate(), box => {
                box.events.refer(this.#collection.events);
            });
            return Props.apply(NoteEventImpl.wrap(this.#context, box), props, "event");
        });
    }
    addAll(events) {
        if (!Array.isArray(events)) {
            return panic(new TypeError("addEvents: expected an array"));
        }
        return this.#context.edit(() => events.map(props => this.add(props)));
    }
    clear() { this.#context.edit(() => this.list().forEach(event => event.box.delete())); }
}
export class ValueEvents {
    #context;
    #collection;
    constructor(context, collection) {
        this.#context = context;
        this.#collection = collection;
    }
    get collection() { return this.#collection; }
    list() {
        return this.#collection.events.pointerHub.incoming()
            .map(({ box }) => ValueEventImpl.wrap(this.#context, asInstanceOf(box, ValueEventBox)))
            .sort((a, b) => a.position - b.position || a.index - b.index);
    }
    add(props) {
        return this.#context.edit(() => {
            const position = Guard.integer(props?.position ?? 0, "event.position");
            const sharing = this.list().filter(event => event.position === position);
            if (sharing.length >= 2) {
                return panic(new RangeError(`event.position: at most two events can share position ${position}`));
            }
            const box = ValueEventBox.create(this.#context.boxGraph, UUID.generate(), box => {
                box.events.refer(this.#collection.events);
                box.position.setValue(position);
                box.index.setValue(sharing.length);
                box.slope.setValue(NaN);
            });
            const event = ValueEventImpl.wrap(this.#context, box);
            const rest = Props.without(props, "position");
            return Props.apply(event, rest, "event");
        });
    }
    addAll(events) {
        if (!Array.isArray(events)) {
            return panic(new TypeError("addEvents: expected an array"));
        }
        return this.#context.edit(() => events.map(props => this.add(props)));
    }
    clear() { this.#context.edit(() => this.list().forEach(event => event.box.delete())); }
}
